import { collection, addDoc } from 'firebase/firestore';
import { db } from './config';
import emailjs from 'emailjs-com';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  message: string;
}

interface QuoteRequestData {
  contact: {
    name: string;
    email: string;
    phone: string;
    company: string;
    state: string;
    notes: string;
  };
  services: {
    service: string;
    quantity: number;
    unit: string;
  }[];
}

/**
 * Submits contact form data to Firestore (triggers Cloud Function email)
 * EmailJS is a backup — errors are logged but don't block submission
 */
export const submitContactForm = async (formData: ContactFormData) => {
  // Save to Firestore — this triggers the Cloud Function that sends the email
  const docRef = await addDoc(collection(db, 'contactSubmissions'), {
    ...formData,
    timestamp: new Date(),
    status: 'new'
  });

  console.log('Contact saved to Firestore:', docRef.id);

  // EmailJS backup — don't block on failure
  try {
    await emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID,
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
      {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        company: formData.company || 'Not provided',
        service: formData.service || 'Not specified',
        message: formData.message
      },
      import.meta.env.VITE_EMAILJS_USER_ID
    );
    console.log('EmailJS backup sent');
  } catch (emailError) {
    console.warn('EmailJS backup failed (Cloud Function will handle email):', emailError);
  }

  return { success: true, id: docRef.id };
};

/**
 * Submits quote request to Firestore (triggers Cloud Function email)
 * EmailJS is a backup — errors are logged but don't block submission
 */
export const submitQuoteRequest = async (data: QuoteRequestData) => {
  // Build a readable summary for email
  const serviceLines = data.services.map(
    s => `${s.service}: ${s.quantity} ${s.unit}${s.quantity !== 1 ? 's' : ''}`
  ).join('\n');

  const summary = [
    'Services Requested:',
    serviceLines,
    '',
    `State: ${data.contact.state || 'Not specified'}`,
    data.contact.notes ? `Notes: ${data.contact.notes}` : '',
  ].filter(Boolean).join('\n');

  // Save to quoteRequests — triggers processQuoteRequest Cloud Function
  const docRef = await addDoc(collection(db, 'quoteRequests'), {
    ...data,
    timestamp: new Date(),
    status: 'new'
  });

  console.log('Quote saved to Firestore:', docRef.id);

  // Also write to contactSubmissions as backup trigger
  try {
    await addDoc(collection(db, 'contactSubmissions'), {
      name: data.contact.name,
      email: data.contact.email,
      phone: data.contact.phone || '',
      company: data.contact.company || '',
      service: 'Online Quote Request',
      message: summary,
      timestamp: new Date(),
      status: 'new'
    });
  } catch (fsError) {
    console.warn('Backup contactSubmissions write failed:', fsError);
  }

  // EmailJS backup — don't block on failure
  try {
    await emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID,
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
      {
        name: data.contact.name,
        email: data.contact.email,
        phone: data.contact.phone || 'Not provided',
        company: data.contact.company || 'Not provided',
        service: 'Online Quote Request',
        message: summary,
      },
      import.meta.env.VITE_EMAILJS_USER_ID
    );
    console.log('EmailJS backup sent');
  } catch (emailError) {
    console.warn('EmailJS backup failed (Cloud Function will handle email):', emailError);
  }

  return { success: true, id: docRef.id };
};
